import { useGetAccountHoldings } from '@/api/generated/trading/trading';
import { PnLIndicator } from '@/components/wealth/PnLIndicator';
import { useLanguage } from '@/i18n/language-hooks';
import { formatCurrency } from '@/lib/utils';
import { Link } from 'react-router-dom';

interface AccountHoldingsTableProps {
  accountId: string;
  /** Compact layout for the detail aside preview. */
  compact?: boolean;
}

export function AccountHoldingsTable({ accountId, compact = false }: AccountHoldingsTableProps) {
  const { t } = useLanguage();
  const { data: holdings = [], isLoading } = useGetAccountHoldings(accountId);

  if (isLoading) {
    return <p className='text-fin-gray-400 text-sm'>{t('common.loading')}</p>;
  }

  if (holdings.length === 0) {
    return <p className='text-fin-gray-400 text-sm'>{t('accounts.holdings.empty')}</p>;
  }

  const visibleHoldings = compact ? holdings.slice(0, 5) : holdings;

  return (
    <div
      className={compact ? 'space-y-2' : 'border-fin-gray-800 overflow-x-auto rounded-xl border'}
    >
      <table
        className={compact ? 'w-full text-left text-sm' : 'w-full min-w-[560px] text-left text-sm'}
      >
        {!compact ? (
          <thead className='bg-fin-gray-900/80 text-fin-gray-400'>
            <tr>
              <th className='px-4 py-3 font-medium'>{t('trade.positions.symbol')}</th>
              <th className='px-4 py-3 text-right font-medium'>{t('trade.positions.quantity')}</th>
              <th className='px-4 py-3 text-right font-medium'>
                {t('trade.positions.marketValue')}
              </th>
              <th className='px-4 py-3 text-right font-medium'>
                {t('trade.positions.unrealizedPnL')}
              </th>
            </tr>
          </thead>
        ) : null}
        <tbody>
          {visibleHoldings.map((holding) => (
            <tr key={holding.id} className={compact ? '' : 'border-fin-gray-800 border-t'}>
              <td className={compact ? 'py-2' : 'px-4 py-3'}>
                <Link
                  to={`/trade/instruments/${encodeURIComponent(holding.assetId)}`}
                  className='text-fin-accent font-medium hover:underline'
                >
                  {holding.assetId}
                </Link>
              </td>
              <td
                className={
                  compact
                    ? 'text-fin-white py-2 text-right tabular-nums'
                    : 'text-fin-white px-4 py-3 text-right tabular-nums'
                }
              >
                {holding.quantity}
              </td>
              <td
                className={
                  compact
                    ? 'text-fin-white py-2 text-right tabular-nums'
                    : 'text-fin-white px-4 py-3 text-right tabular-nums'
                }
              >
                {formatCurrency(holding.marketValue ?? holding.costBasis, holding.currency)}
              </td>
              <td className={compact ? 'py-2 text-right' : 'px-4 py-3 text-right'}>
                <PnLIndicator
                  amount={holding.unrealizedPnL ?? 0}
                  formatted={(() => {
                    const pnl = holding.unrealizedPnL ?? 0;
                    const formatted = formatCurrency(Math.abs(pnl), holding.currency);
                    return pnl > 0 ? `+${formatted}` : pnl < 0 ? `-${formatted}` : formatted;
                  })()}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {compact && holdings.length > visibleHoldings.length ? (
        <p className='text-fin-gray-500 text-xs'>
          {t('accounts.holdings.more', { count: holdings.length - visibleHoldings.length })}
        </p>
      ) : null}
    </div>
  );
}
